import type { AmplienceTopLevelNavigationCategory, TopLevelNavCategoryItem } from '../types';

import { useMemo } from 'react';

import { createUniqueIdFactory, isExternalLink, isValidNavigationLink } from '../utils';

export const useTopLevelNavigationCategories = (
  amplienceCategories: AmplienceTopLevelNavigationCategory[] = []
): TopLevelNavCategoryItem[] => {
  const categories = useMemo(() => {
    const categoryId = createUniqueIdFactory();

    return amplienceCategories
      .filter(category => category.label)
      .map(category => {
        const id = categoryId(category.id ?? category.label?.toLowerCase() ?? 'category');
        const columnId = createUniqueIdFactory();

        return {
          id,
          label: category.label ?? '',
          // No fallback: a category without a link renders as plain text.
          url: category.href || undefined,
          columns: (category.columns ?? [])
            .filter(column => column.links?.some(isValidNavigationLink))
            .map(column => {
              const columnKey = columnId(`${id}-${column.heading || column.href || 'column'}`);
              const linkId = createUniqueIdFactory();

              return {
                id: columnKey,
                heading: column.heading ? { label: column.heading, url: column.href } : undefined,
                groups: [
                  {
                    id: `${columnKey}-links`,
                    links: (column.links ?? []).filter(isValidNavigationLink).map(link => ({
                      id: linkId(`${link.href}-${link.label}`),
                      label: link.label,
                      url: link.href,
                      isExternal: isExternalLink(link.href, undefined, link.target),
                      target: link.target,
                      color: link.color,
                      fontWeight: link.fontWeight,
                      featuredLink: link.featuredLink,
                    })),
                  },
                ],
              };
            }),
        };
      });
  }, [amplienceCategories]);

  return categories;
};
